<!-- eslint-disable perfectionist/sort-imports -->
<!-- eslint-disable perfectionist/sort-modules -->
<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { toast } from 'vue3-toastify';
import { useRouter } from 'vue-router';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import type { CampaignManagementApi } from '~/services/campaign-management-api/campaign-management-api.types';
import {
  campaignManagementService,
  CreateCampaignRejectedError,
} from '~/services/campaign-management-api/campaign-management-api';
import getErrorMessage from '~/utils/getErrorMessage';

import type {
  CampaignFormErrors,
  FilterSelectOption,
  IdentificationData,
  PeriodData,
  SegmentationData,
} from '../types';

import CampaignCreateResultModal from '../components/CampaignCreateResultModal.vue';
import IdentificationSection from '../components/form/IdentificationSection.vue';
import PeriodSection from '../components/form/PeriodSection.vue';
import SegmentationSection from '../components/form/SegmentationSection.vue';
import {
  mapCreateCampaignPayload,
  mapIncentiveSelectOptions,
} from '../mappers/campaignMappers';
import { isCampaignStartDateValid } from '../utils';

type ResultModalMode = 'all-rejected' | 'partial';

const router = useRouter();

const isSaving = ref(false);
const isLoadingIncentives = ref(true);
const incentiveOptions = ref<FilterSelectOption[]>([]);
const resultModal = ref<{
  acceptedCount: number;
  mode: ResultModalMode;
  rejected: CampaignManagementApi.CreateCampaignRejectedTarget[];
  show: boolean;
}>({
  acceptedCount: 0,
  mode: 'partial',
  rejected: [],
  show: false,
});

const identification = reactive<IdentificationData>({
  hypothesis: '',
  incentive: '',
  name: '',
});

const segmentation = reactive<SegmentationData>({
  brands: [],
  ccbMaxPerClient: null,
  ccbMin: null,
  delayMax: null,
  delayMin: null,
  partnerIds: [],
});

const period = reactive<PeriodData>({
  endDate: '',
  startDate: '',
});

const errors = ref<CampaignFormErrors>({});

async function loadIncentiveOptions() {
  isLoadingIncentives.value = true;

  try {
    const data = await campaignManagementService.listIncentives({
      page: 1,
      page_size: 100,
      status: 'ACTIVE',
    });

    incentiveOptions.value = mapIncentiveSelectOptions(data.items);
  } catch {
    incentiveOptions.value = [];
    toast.error('Não foi possível carregar os incentivos. Tente novamente.');
  } finally {
    isLoadingIncentives.value = false;
  }
}

function scrollToFirstError() {
  const firstInvalid = document.querySelector(
    '.is-invalid, .invalid-feedback, .chips-box-error, .fs-error',
  );
  firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function openResultModal(
  mode: ResultModalMode,
  rejected: CampaignManagementApi.CreateCampaignRejectedTarget[],
  acceptedCount: number,
) {
  resultModal.value = {
    acceptedCount,
    mode,
    rejected,
    show: true,
  };
}

function validate(): boolean {
  const e: CampaignFormErrors = {};

  if (!identification.name.trim()) {
    e.name = 'Nome da campanha é obrigatório.';
  }

  if (!identification.incentive) {
    e.incentive = 'Selecione um incentivo.';
  }

  if (!identification.hypothesis.trim()) {
    e.hypothesis = 'Descreva a hipótese ou objetivo do teste.';
  }

  if (!period.startDate) {
    e.startDate = 'Informe a data de início.';
  } else if (!isCampaignStartDateValid(period.startDate)) {
    e.startDate = 'A data de início deve ser no mínimo amanhã (D+1).';
  }

  if (!period.endDate) {
    e.endDate = 'Informe a data de término.';
  } else if (period.startDate && period.endDate <= period.startDate) {
    e.endDate = 'A data de término deve ser posterior à data de início.';
  }

  if (segmentation.partnerIds.length === 0) {
    e.partners = 'Adicione pelo menos um parceiro.';
  }

  if (segmentation.brands.length === 0) {
    e.brands = 'Selecione pelo menos uma marca elegível.';
  }

  if (segmentation.delayMin == null) {
    e.delayMin = 'Informe o atraso mínimo em dias.';
  }

  if (segmentation.delayMax == null) {
    e.delayMax = 'Informe o atraso máximo em dias.';
  } else if (
    segmentation.delayMin != null
    && segmentation.delayMax < segmentation.delayMin
  ) {
    e.delayMax = 'O atraso máximo deve ser maior ou igual ao mínimo.';
  }

  if (segmentation.ccbMin == null) {
    e.ccbMin = 'Informe o mínimo de CCBs por parceiro.';
  }

  if (segmentation.ccbMaxPerClient == null) {
    e.ccbMaxPerClient = 'Informe o máximo de CCBs por parceiro.';
  }

  errors.value = e;
  return Object.keys(e).length === 0;
}

async function submitCampaign() {
  if (!validate()) {
    scrollToFirstError();
    toast.error('Corrija os erros antes de ativar.');
    return;
  }

  isSaving.value = true;

  try {
    const selectedIncentive = incentiveOptions.value.find(
      (option) => option.id === identification.incentive,
    );
    const payload = mapCreateCampaignPayload(
      identification,
      period,
      segmentation,
      selectedIncentive?.description,
    );

    const result = await campaignManagementService.createCampaign(payload);

    if (result.status === 207) {
      openResultModal('partial', result.data.rejected, result.data.accepted.length);
      return;
    }

    toast.success('Campanha criada com sucesso!');
    router.push('/revalida-campaigns');
  } catch (error) {
    if (error instanceof CreateCampaignRejectedError) {
      openResultModal('all-rejected', error.rejected, error.accepted.length);
      return;
    }

    toast.error(getErrorMessage(error));
  } finally {
    isSaving.value = false;
  }
}

function closeResultModal() {
  const { mode } = resultModal.value;
  resultModal.value.show = false;

  if (mode === 'partial') {
    router.push('/revalida-campaigns');
    return;
  }

  segmentation.partnerIds = [];
  errors.value = {};
}

function handleCancel() {
  router.push('/revalida-campaigns');
}

async function handleReviewAndActivate() {
  await submitCampaign();
}

onMounted(() => {
  document.title = 'Nova Campanha';
  loadIncentiveOptions();
});
</script>

<template>
  <DashboardLayout
    title="Revalida Instalação"
    description="Nova Campanha"
    subtitle="Defina identificação, segmentação e período de vigência antes de ativar a campanha."
  >
    <template #actions>
      <div class="d-flex gap-2">
        <button
          type="button"
          class="btn btn-ghost-secondary"
          :disabled="isSaving"
          @click="handleCancel"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="isSaving"
          @click="handleReviewAndActivate"
        >
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" />
          Revisar e ativar
        </button>
      </div>
    </template>

    <div class="row row-cards">
      <div class="col-12">
        <div class="card">
          <IdentificationSection
            v-model="identification"
            :errors="errors"
            :incentive-options="incentiveOptions"
            :is-loading-incentives="isLoadingIncentives"
          />
          <SegmentationSection
            v-model="segmentation"
            :errors="errors"
          />
          <PeriodSection
            v-model="period"
            :errors="errors"
          />
        </div>
      </div>
    </div>

    <CampaignCreateResultModal
      :show="resultModal.show"
      :mode="resultModal.mode"
      :rejected="resultModal.rejected"
      :accepted-count="resultModal.acceptedCount"
      @close="closeResultModal"
    />
  </DashboardLayout>
</template>
